import { useState, useEffect, useRef, useMemo, createContext, useCallback } from 'react';
import { useSearchParams, useParams, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import * as http from '../../utils/http';
import * as translator from '../../utils/translator';
import HeaderController from '../../components/Headers/HeaderController';
import Footer from '../../components/Footer/Footer';
import StickyNotification from '../../components/StickyNotification/StickyNotification';
import LoadingModal from '../../components/Modals/LoadingModal/LoadingModal';
import ServerError from '../../components/ServerError/ServerError';
import LatePayment from '../../components/ExpressDelivery/LatePayment/LatePayment';
import { useCartContext } from '../../customHooks/useCart';
import CarePlusShippingCartCard from '../CarePlusShared/CarePlusShippingCartCard';
import CarePlusCostBreakdown from '../CarePlusShared/CarePlusCostBreakdown';
import { getCarePlusShippingCartProps } from '../CarePlusShared/carePlusShippingCartProps';
import { getCarePlusCostBreakdownRows } from '../CarePlusShared/carePlusCostBreakdownRows';
import { getCarePlusCostBreakdownInput } from '../CarePlusShared/carePlusCostBreakdownInput';
import { cameToPaymentFromShipping, resetPaymentOriginOnArrival } from './paymentBackOrigin';
import { lineFromProducts } from '../ChoosePlanCarePlus/useChoosePlanCarePlus';
import { MainTracker } from '../../PixelTrackers/MainTracker';
import { CLICK_EXPAND_PRODUCTS } from '../../utils/consts';
import PaymentV3CarePlus from '../v3/PaymentV3CarePlus/PaymentV3CarePlus';
import { showsCarePlusShippingFAQ } from '../CarePlusShared/showsCarePlusShippingFAQ';
import TrustedShopsBadge from '../../components/Trustedshops/TrustedShopsBadge';
import { useAppContext } from '../../providers/AppProvider';

import { usePaymentData } from './usePaymentData';
import AdyenCheckoutForm from './CheckoutForms/AdyenCheckoutForm';
import StripeCheckoutForm from './CheckoutForms/StripeCheckoutForm';
import OrderSidebar, { MobileCartView } from './OrderSidebar';
import { REFUSAL_REASON_CODES } from './paymentConstants';
import type { PaymentCallbacks, UserClickTracking, PaymentResult } from './paymentTypes';
import IdVerificationModal from './IdVerificationModal';
import { isStripeProvider } from './paymentTypes';
import "./PaymentPage.css"

// ============================================================================
// Context Export
// ============================================================================

export const PaymentDataContext = createContext<any>(null);

// ============================================================================
// Component
// ============================================================================

export default function PaymentPage() {
    // ============================================================================
    // Route & Search Params
    // ============================================================================
  
    const { country, language, order_hash_id: hashID } = useParams<{ country: string; language: string; order_hash_id: string }>();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigationType = useNavigationType();
    const navigate = useNavigate();
    const { cartCurrency, updateCart, restoreCartFromOrder } = useCartContext();
    const { headerVersion, setHeaderVersion } = useAppContext();

    // ============================================================================
    // Local UI State
    // ============================================================================

    const [showStickyNotification, setShowStickyNotification] = useState({ show: false, title: '', content: '', level: 'error', autoFade: true, refresh: false });
    const [showLatePayment, setShowLatePayment] = useState(false);
    const [latePaymentReason, setLatePaymentReason] = useState<string | null>(null);
    const [isNextDayAvailable, setIsNextDayAvailable] = useState(false);
    const [rxProductFinalPrice, setRxProductFinalPrice] = useState(0);
    const [paymentRefused, setPaymentRefused] = useState(false);
    const [showIdVerificationModal, setShowIdVerificationModal] = useState(false);
    const [isIdVerified, setIsIdVerified] = useState(false);
    const [verificationCollapseKey, setVerificationCollapseKey] = useState(0);
    const [activeVerificationPaymentMethodType, setActiveVerificationPaymentMethodType] = useState<string>('');

    // ============================================================================
    // Tracking Refs
    // ============================================================================

    const openedTooltipsRef = useRef(new Set<string>());
    const userClicksRef = useRef(new Set<string>());
    const tracking = useMemo<UserClickTracking>(() => ({ openedTooltips: openedTooltipsRef.current, userClicks: userClicksRef.current }), []);

    // ============================================================================
    // Use Payment Data Hook
    // ============================================================================

    const {
        loading,
        setLoading,
        error,
        setError,
        paymentServiceProvider,
        orderState,
        orderWindowDetails,
        shippingFee,
        setShippingFee,
        OTCProducts,
        otcDiscount,
        currencyRate,
        destinationCountry,
        isCannabisCatalog,
        testLabSampleType,
        productsGroupedByPharmacy,
        adyenInitData,
        stripeInitData,
        paymentContext,
        showDelayedCaptureDisclaimer,
        handleReviveOutOfStock,
        consultationType,
        flow,
        paymentTypesRequiringIdVerification,
        idVerificationPaymentMethodNames,
        isCustomerIdVerified,
        cannabisLimitsBlocked,
        hidePaypalDisclaimer,
        isCarePlusCatalog,
        cartRef,
    } = usePaymentData({
        country: country!,
        language: language!,
        hashID: hashID!,
        searchParams,
        isRepay: searchParams.get('repay') === 'true',
        disallowPaymentMethods: searchParams.get('disallow')?.split(',') || [],
        paymentRefused,
        onCannabisLimitsBlocked: (message: string) => handleErrorNotification('', message, 'error'),
    });

    // Set headerVersion based on isCannabisCatalog (in useEffect to avoid updating parent state during render)
    useEffect(() => {
        setHeaderVersion(isCannabisCatalog ? 'v2' : 'v1');
    }, [isCannabisCatalog, setHeaderVersion]);

    const paymentCostBreakdownRef = useRef<HTMLDivElement>(null);
    const [costBreakdownOpenRequest, setCostBreakdownOpenRequest] = useState(0);
    const paymentCart = cartRef.current;
    const carePlusT = (key: string, fallback: string) => translator.translate(language!, 'choose_plan_care_plus', key) || fallback;
    // Care+ catalog (as on Shipping) or a Care+ plan on the cart — V3 one-time carts have no plan fee.
    const isCarePlusPayment = isCarePlusCatalog || Number(paymentCart?.care_plus_plan_fee ?? 0) > 0;
    const paymentCarePlusCardProps = paymentCart ? getCarePlusShippingCartProps(paymentCart, isCarePlusPayment).cardProps : null;
    const paymentCarePlusMedication = paymentCarePlusCardProps && !paymentCart?.only_prescription
        ? lineFromProducts(paymentCart?.products ?? [])
        : null;
    const paymentCostBreakdownRows = paymentCart && paymentCarePlusCardProps && getCarePlusCostBreakdownRows({
        t: carePlusT,
        ...getCarePlusCostBreakdownInput(paymentCart),
    });

    // Initialize isIdVerified from server response so verified customers skip the modal
    useEffect(() => {
        if (isCustomerIdVerified) {
            setIsIdVerified(true);
        }
    }, [isCustomerIdVerified]);

    const effectivePaymentTypesRequiringIdVerification = paymentTypesRequiringIdVerification;

    // ============================================================================
    // Error Notification Handler
    // ============================================================================

    const handleErrorNotification = useCallback((title: string, content: string, level = 'error') => {
        if (!title && !content) {
        setShowStickyNotification({ show: false, title: '', content: '', level, autoFade: true, refresh: false });
        } else {
        setShowStickyNotification({ show: true, title, content, level, autoFade: true, refresh: true });
        }
    }, []);

    // Stays on screen until the customer closes it
    const handleStickyErrorNotification = useCallback((title: string, content: string) => {
        setShowStickyNotification({ show: true, title, content, level: 'error', autoFade: false, refresh: true });
    }, []);

    // ============================================================================
    // Restore Cart
    // ============================================================================
    const restoreCart = () => restoreCartFromOrder(country!, language!, hashID!);


    // ============================================================================
    // Handle Payment Back
    // ============================================================================

    const relocationString = `/${country}/account/my-orders/${hashID}/details`;

    const handlePaymentBack = async () => {
        try {
            if (!cameToPaymentFromShipping(hashID, location.state)) {
            navigate({ pathname: relocationString, search: 'showRedirectModal=true' }, { replace: true });
            return;
            }

            // consultationType and flow need to come from usePaymentData
            const isVideoConsultation = consultationType === 'video';

            if (isVideoConsultation) {
            searchParams.set('rc', 'true');
            MainTracker.track('custom', 'Payment Back', { backTo: 'CT', consultationType, flow, paymentServiceProvider});
            const { mention } = await restoreCart();
            navigate({
                pathname: `/select_treatment/${country}/${language}/${mention}`,
                search: searchParams.toString(),
            }, { replace: true });
            } else {
            searchParams.set('rc', 'true');
            MainTracker.track('custom', 'Payment Back', { backTo: 'Shipping', consultationType, flow, paymentServiceProvider});
            await restoreCart();
            navigate({ pathname: `/shipping/${country}/${language}`, search: searchParams.toString() }, { replace: true });
            }
        } catch (error) {
            window.clientLogger.error('error in handlePaymentBack', error);
            navigate({ pathname: relocationString, search: 'showRedirectModal=true' }, { replace: true });
        }
    };
    
    // ============================================================================
    // URL Param Error Handling Effect
    // ============================================================================

    useEffect(() => {
        const refusalReason = searchParams.get('rr');
        const ec = searchParams.get('ec');
        const stripeError = searchParams.get('stripeErrorMessage');

        if (ec === 'rp') {
            handleErrorNotification(
                translator.translate(language!, 'payment', 'cart_changed/title'),
                translator.translate(language!, 'payment', 'cart_changed/content'),
                'success'
            );
        }

        if (REFUSAL_REASON_CODES.includes(refusalReason || '')) {
            handleErrorNotification(
                translator.translate(language!, 'default', 'StickyNotification/danger_title'),
                translator.translate(language!, 'default', 'StickyNotification/adyen_message_refused')
            );
        }

        if (stripeError) {
            handleErrorNotification(
                translator.translate(language!, 'default', 'StickyNotification/danger_title'),
                stripeError
            );
        }
    }, [searchParams, language, handleErrorNotification]);

    // ============================================================================
    // Express Prescription Review Unavailable Handler
    // ============================================================================

    const cancelExpressPrescriptionReview = () => {
        http.postToServer(`v2/orders/cancel_express_prescription_review/${hashID}`, {}, [http.MIDDLEWARES.CUSTOMER_ID])
        .then((response) => {
            updateCart({ treatment_fee: orderWindowDetails.treatmentFee - response.data?.expressPrescriptionReviewFee });
            setShowStickyNotification({
                show: true,
                title: translator.translate(language!, 'default', 'StickyNotification/express_prescription_review_unavailable_title'),
                content: translator.translate(language!, 'default', 'StickyNotification/express_prescription_review_unavailable_description'),
                level: 'warning',
                autoFade: true,
                refresh: true,
            })
        })
        .catch((err) => {
            window.clientLogger.error('error in cancelExpressPrescriptionReview', err);
            setError(500);
        });
    }

    // ============================================================================
    // Handle Payment Back Pop State
    // ============================================================================

    useEffect(() => {
        resetPaymentOriginOnArrival(hashID, location.state, navigationType);
    }, []);

    useEffect(() => {
        // don't use window.history.pushState to prevent tracking errors (VWO overrides it)
        History?.prototype?.pushState?.call(window.history, null, "", window.location.href);

        const onPopState = () => {
            setLoading(true);
            handlePaymentBack().then(() => {
                setLoading(false);
            });
        };
    
        window.addEventListener("popstate", onPopState);
    
        return () => window.removeEventListener("popstate", onPopState);
    }, []);

    // ============================================================================
    // Email CTA Tracking Effect
    // ============================================================================

    useEffect(() => {
        //first on land, if has email parmas then send event to mixpanel
        const ctaType = searchParams.get('CTA_type');
        const hashIdCTAKey = 'last_email_order_id_cta';  // local storage key

         //The order id of the order that the email was sent from and directed to the payment page with the params.
        const lastEmailFromStorage = localStorage.getItem(hashIdCTAKey);
        const [storageHashID, storageCTA] = lastEmailFromStorage?.split('&') || [];

        const orderHashIDFromUrl = searchParams.get('order_hash_id') ?? 'url param is missing';

        if (ctaType && (orderHashIDFromUrl !== storageHashID || storageCTA !== ctaType)) {
            const emailParams = {
                step_number: searchParams.get('step_number') ?? 'url param is missing',
                CTA_type: ctaType,
                country: searchParams.get('country') ?? 'url param is missing',
                product_category: searchParams.get('product_category') ?? 'url param is missing',
                order_hash_id: orderHashIDFromUrl,
                "Payment Service Provider": paymentServiceProvider,
            };

            //remember this order id to avoid sending the event again.
            localStorage.setItem(hashIdCTAKey, `${hashID}&${ctaType}`);

            //Send event to mixpanel
            MainTracker.track('custom', 'Cart abandonment CTA clicked', emailParams);
        }
    }, []);

    // ============================================================================
    // Payment Callbacks
    // ============================================================================

    const paymentCallbacks: PaymentCallbacks = {
        onPaymentSuccess: (result: PaymentResult) => {result.isAuthorized = true;},
        onPaymentRefused: (result: PaymentResult) => {result.isAuthorized = false;},
        onPaymentAction: async () => {},
        onExpressUnavailable: (reason, nextDayAvailable) => {
            setShowLatePayment(true);
            setLatePaymentReason(reason);
            setIsNextDayAvailable(nextDayAvailable);
        },
        onExpressPrescriptionReviewUnavailable: cancelExpressPrescriptionReview,
        onError: () => setError(500),
        showErrorNotification: (title, content, options) => options?.sticky
            ? handleStickyErrorNotification(title, content)
            : handleErrorNotification(title, content),
        setPaymentRefused,
        onOutOfStock: handleReviveOutOfStock,
        onRefreshPage: () => {
            window.location.href = `/payment/${country}/${language}/${hashID}?ec=rp`;
        },
        onIdVerificationRequired: (paymentMethodType?: string) => {
            // Only show modal if ID verification is required for this payment type
            if (paymentMethodType && effectivePaymentTypesRequiringIdVerification.includes(paymentMethodType)) {
                MainTracker.track('custom', 'ID Check Modal Selection', { Decision: 'Proceed to ID Check' });
                setActiveVerificationPaymentMethodType(paymentMethodType);
                setShowIdVerificationModal(true);
                // Reset verification when modal opens
                setIsIdVerified(false);
            }
        },
        onTriggerPaymentSubmit: () => {
            // This will be set by the checkout forms
        },
        isIdVerified,
        verificationCollapseKey,
        paymentTypesRequiringIdVerification: effectivePaymentTypesRequiringIdVerification,
        idVerificationPaymentMethodNames,
        isExpressOrder: orderState?.is_express_shipping || orderState?.is_express_prescription_review,
        hidePaypalDisclaimer,
    };

    // ============================================================================
    // Delivery Option Handler
    // ============================================================================

    const onSelectDeliveryOption = useCallback((deliveryOption: string) => {
        if (deliveryOption === 'express') {
            window.eventsTracking.track({ e: 'Uber late payment stay express' });
            setShowLatePayment(false);
        } else if (deliveryOption === 'next_day') {
            http.postToServer(`v2/orders/switch_to_next_day_urbify/${hashID}`, {}, [http.MIDDLEWARES.CUSTOMER_ID])
            .then(() => {
                window.eventsTracking.track({ e: 'Urbify late payment switch to next day' });
                setShowLatePayment(false);
            })
            .catch((err) => {
                window.clientLogger.error('error in switchToNextDayUrbify', err);
                setError(500);
            });
        } else {
            http.postToServer(`v2/orders/cancel_express_delivery/${hashID}`, {}, [http.MIDDLEWARES.CUSTOMER_ID])
            .then((response) => {
                window.eventsTracking.track({ e: 'Uber late payment switch to dhl' });
                setShowLatePayment(false);
                setShippingFee(0);
                updateCart({ custom_shipping_fee: 0, bonus_value: response.data?.updatedBonusValue || 0 });
            })
            .catch((err) => {
                window.clientLogger.error('error in selectDeliveryOption', err);
                setError(500);
            });
        }
    }, [hashID, setShippingFee, updateCart, setError]);

    const paymentOptions = !cannabisLimitsBlocked && (
        <>
            {!isCarePlusPayment && (
                <p className="orderWindow-container-title">
                    {translator.translate(language!, 'default', 'OrderWindow/payment_options')}
                </p>
            )}
            {isCarePlusPayment ? (
                <div className="payment-v3-care-plus__card-method">
                    <div className="payment-v3-care-plus__card-method-header">
                        <span className="payment-v3-care-plus__card-method-title">{translator.translate(language!, 'payment', 'placeholders/paymentMethod')}</span>
                        <p className="payment-v3-care-plus__card-method-subtitle">{translator.translate(language!, 'payment', 'requiredFieldsNote')}</p>
                    </div>
                    {paymentServiceProvider === 'adyen' && adyenInitData && paymentContext && (
                        <AdyenCheckoutForm adyenInitData={adyenInitData} paymentContext={paymentContext} callbacks={paymentCallbacks} tracking={tracking} />
                    )}
                    {isStripeProvider(paymentServiceProvider) && stripeInitData && paymentContext && (
                        <StripeCheckoutForm stripeInitData={stripeInitData} paymentContext={paymentContext} callbacks={paymentCallbacks} tracking={tracking} hideBillingCountry />
                    )}
                </div>
            ) : (
                <>
                    {paymentServiceProvider === 'adyen' && adyenInitData && paymentContext && (
                        <AdyenCheckoutForm adyenInitData={adyenInitData} paymentContext={paymentContext} callbacks={paymentCallbacks} tracking={tracking} />
                    )}
                    {isStripeProvider(paymentServiceProvider) && stripeInitData && paymentContext && (
                        <StripeCheckoutForm stripeInitData={stripeInitData} paymentContext={paymentContext} callbacks={paymentCallbacks} tracking={tracking} />
                    )}
                </>
            )}
        </>
    );

    // ============================================================================
    // Render: Loading
    // ============================================================================

    if (loading) {
        return <LoadingModal />;
    }

    // ============================================================================
    // Render: Error
    // ============================================================================

    if (error) {
        return (
        <>
            <HeaderController showSteps={false} stage="Questionnaire" headerVersion={headerVersion} />
            <ServerError status={error} />
        </>
        );
    }

    // ============================================================================
    // Render: Main Content
    // ============================================================================

    return (
        <>
        <StickyNotification
            level={showStickyNotification.level}
            showNotification={showStickyNotification.show}
            title={showStickyNotification.title}
            onClick={(val: any) => setShowStickyNotification(val)}
            content={showStickyNotification.content}
            autoFade={showStickyNotification.autoFade}
            refresh={showStickyNotification.refresh}
            onShown={(() => setShowStickyNotification(prev => ({ ...prev, refresh: false }))) as any}
        />

        <LatePayment
            language={language!}
            isShown={showLatePayment}
            setIsShown={setShowLatePayment}
            onSelectDeliveryOption={onSelectDeliveryOption}
            reason={latePaymentReason || ''}
            isNextDayAvailable={isNextDayAvailable}
        />

        <IdVerificationModal
            isOpen={showIdVerificationModal}
            onClose={() => {
                MainTracker.track('custom', 'ID Check Modal Selection', { Decision: 'Close modal' });
                setShowIdVerificationModal(false);
                // Collapse the selected payment method since verification was not completed
                // Adyen: click the first non-selected payment method header
                setTimeout(() => {
                    const headers = document.querySelectorAll('.adyen-checkout__payment-method__header');
                    const firstNonSelected = Array.from(headers).find((header) => {
                        const parentMethod = header.closest('.adyen-checkout__payment-method');
                        return parentMethod && !parentMethod.classList.contains('adyen-checkout__payment-method--selected');
                    });
                    if (firstNonSelected) {
                        (firstNonSelected as HTMLElement).click();
                    }
                }, 50);

                // Stripe: trigger collapse via key change
                setVerificationCollapseKey((prev) => prev + 1);
            }}
            onSubmit={() => {
                setIsIdVerified(true);
                // Eagerly mutate so checkout forms see the updated value
                // before the React state update triggers a re-render
                paymentCallbacks.isIdVerified = true;
                if (paymentCallbacks.onTriggerPaymentSubmit) {
                    paymentCallbacks.onTriggerPaymentSubmit();
                }
                setShowIdVerificationModal(false);
            }}
            hashID={hashID!}
            language={language!}
            paymentMethodName={idVerificationPaymentMethodNames[activeVerificationPaymentMethodType] || ''}
        />

        {!isCarePlusPayment && (
            <HeaderController language={language} stage="Payment" flow={flow} headerVersion={headerVersion} />
        )}

        {isCarePlusPayment ? (
            <PaymentV3CarePlus
                t={carePlusT}
                showFaq={showsCarePlusShippingFAQ(country)}
                trustBar={<TrustedShopsBadge className="w-full max-w-none" country={country!} language={language!} variant="v3" />}
                paymentOptions={paymentOptions}
                cartSummary={paymentCarePlusCardProps && (
                    <CarePlusShippingCartCard
                        {...paymentCarePlusCardProps}
                        t={carePlusT}
                        medication={paymentCarePlusMedication}
                        currencyLocale={cartCurrency.locale}
                        currencyRate={currencyRate ? Number(currencyRate) : 1}
                        fullTierName
                        defaultShowNextMonthDetails
                    />
                )}
                costBreakdown={paymentCostBreakdownRows && (
                    <div ref={paymentCostBreakdownRef}>
                        <CarePlusCostBreakdown
                            t={carePlusT}
                            rows={paymentCostBreakdownRows}
                            currencyLocale={cartCurrency.locale}
                            currencyRate={currencyRate ? Number(currencyRate) : 1}
                            openRequest={costBreakdownOpenRequest}
                            onToggle={(isOpen) => MainTracker.track("custom", "Click Cost Details", {
                                page: "Payment",
                                state: isOpen ? "open" : "close",
                            })}
                        />
                    </div>
                )}
                onOpenCostBreakdown={() => {
                    setCostBreakdownOpenRequest((current) => current + 1);
                    paymentCostBreakdownRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                cta={null}
            />
        ) : (
        <div className="payment-container">
            <div className="payment-title">
            <p>{translator.translate(language!, 'default', 'OrderWindow/payment_title')}</p>
            </div>

            <div className="payment-main-content">
            {/* Left Panel: Payment Options */}
            <div className="left-panel">
                <MobileCartView
                    country={country!}
                    language={language!}
                    orderState={orderState}
                    orderWindowDetails={orderWindowDetails}
                    OTCProducts={OTCProducts}
                    otcDiscount={otcDiscount}
                    shippingFee={shippingFee}
                    currencyRate={currencyRate}
                    currencySign={cartCurrency.currencySign || ''}
                    isCannabisCatalog={isCannabisCatalog}
                    testLabSampleType={testLabSampleType}
                    productsGroupedByPharmacy={productsGroupedByPharmacy}
                    onExpandProducts={() => userClicksRef.current.add(CLICK_EXPAND_PRODUCTS)}
                    onTooltipOpen={(id) => openedTooltipsRef.current.add(id)}
                    showDelayedCaptureDisclaimer={showDelayedCaptureDisclaimer}
                />

                {!cannabisLimitsBlocked && (
                    <>
                    <p className="orderWindow-container-title">
                        {translator.translate(language!, 'default', 'OrderWindow/payment_options')}
                    </p>

                    {/* Adyen Checkout */}
                    {paymentServiceProvider === 'adyen' && adyenInitData && paymentContext && (
                        <AdyenCheckoutForm
                            adyenInitData={adyenInitData}
                            paymentContext={paymentContext}
                            callbacks={paymentCallbacks}
                            tracking={tracking}
                        />
                    )}

                    {/* Stripe Checkout */}
                    {isStripeProvider(paymentServiceProvider) && stripeInitData && paymentContext && (
                        <StripeCheckoutForm
                            stripeInitData={stripeInitData}
                            paymentContext={paymentContext}
                            callbacks={paymentCallbacks}
                            tracking={tracking}
                        />
                    )}
                    </>
                )}
            </div>

            {/* Right Panel: Order Summary */}
            <PaymentDataContext.Provider
                value={{
                    destinationCountry,
                    currencyRate,
                    currencySign: cartCurrency.currencySign,
                }}
            >
                <div className="right-panel">
                    <OrderSidebar
                        country={country!}
                        language={language!}
                        orderState={orderState}
                        orderWindowDetails={orderWindowDetails}
                        OTCProducts={OTCProducts}
                        otcDiscount={otcDiscount}
                        shippingFee={shippingFee}
                        currencyRate={currencyRate}
                        currencySign={cartCurrency.currencySign || ''}
                        rxProductFinalPrice={rxProductFinalPrice}
                        setRxProductFinalPrice={setRxProductFinalPrice}
                        isCannabisCatalog={isCannabisCatalog}
                        testLabSampleType={testLabSampleType}
                        productsGroupedByPharmacy={productsGroupedByPharmacy}
                        onExpandProducts={() => userClicksRef.current.add(CLICK_EXPAND_PRODUCTS)}
                        onTooltipOpen={(id) => openedTooltipsRef.current.add(id)}
                        showDelayedCaptureDisclaimer={showDelayedCaptureDisclaimer}
                    />
                </div>
            </PaymentDataContext.Provider>
            </div>
        </div>
        )}

        <Footer language={language!} page="payment" />
        </>
    );
}